import { apiSlice } from './apiSlice';
// import { setUnreadCount } from '../store/slices/notificationSlice';

export interface NotificationTemplate {
  notification_template_id: string;
  customer_id?: string;
  client_id?: string;
  entity_type: string;
  action_type: string;
  template_content: string;
  notification_type: string;
  status: number;
  created_id: string;
  created_at: string;
  updated_id?: string;
  updated_at?: string;
}

export interface Notification {
  notification_id: string;
  customer_id?: string;
  client_id?: string;
  recipient_id?: string;
  notification_object_id?: string;
  notification_action_id?: string;
  message: string;
  notification_type: string;
  delivery_status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  read_at?: string | null;
  delivered_at?: string;
  expires_at?: string;
  created_id: string;
  created_at: string;
  updated_id?: string;
  updated_at?: string;
}

export interface UserNotificationPreference {
  user_notification_preference_id: string;
  notification_template_id: string;
  user_id?: string;
  template: NotificationTemplate;
  status: number;
  created_id: string;
  created_at: string;
  updated_id?: string;
  updated_at?: string;
}

export interface NotificationEnums {
  entity_types: string[];
  entity_categories: Record<string, Array<{ type: string; label: string }>>;
  action_types: string[];
  notification_types: string[];
  delivery_statuses: string[];
  priorities: string[];
}

export const notificationApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ==================== Templates ====================
    getNotificationTemplates: builder.query<
      NotificationTemplate[],
      {
        entity_type?: string;
        action_type?: string;
        notification_type?: string;
        status?: number;
      }
    >({
      query: (params) => ({
        url: `/api/notifications/templates`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => response.templates,
      providesTags: ['NotificationTemplates'],
    }),

    createNotificationTemplate: builder.mutation<any, Partial<NotificationTemplate>>({
      query: (template) => ({
        url: `/api/notifications/templates`,
        method: 'POST',
        body: template,
      }),
      invalidatesTags: ['NotificationTemplates'],
    }),

    deleteNotificationTemplate: builder.mutation<any, string>({
      query: (templateId) => ({
        url: `/api/notifications/templates/${templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NotificationTemplates'],
    }),

    // ==================== Notifications ====================
    getNotifications: builder.query<
      Notification[],
      {
        delivery_status?: string;
        priority?: string;
        notification_type?: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: (params) => ({
        url: `/api/notifications`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: { notifications: Notification[] }) => response.notifications,
      keepUnusedDataFor: 120,
      providesTags: ['Notifications'],
    }),

    // getUnreadCount: builder.query<number, void>({
    //   query: () => ({
    //     url: `/api/notifications/unread-count`,
    //     method: 'GET',
    //   }),
    //   transformResponse: (response: any) => response.unread_count,
    //   providesTags: ['NotificationCount'],
    //   async onQueryStarted(arg, { dispatch, queryFulfilled }) {
    //     try {
    //       const { data } = await queryFulfilled;
    //       dispatch(setUnreadCount(data));
    //     } catch (err) {
    //       // Ignore errors
    //     }
    //   },
    // }),

    markNotificationAsRead: builder.mutation<any, string>({
      query: (notificationId) => ({
        url: `/api/notifications/${notificationId}/read`,
        method: 'POST',
      }),
      invalidatesTags: ['Notifications', 'NotificationCount'],
    }),

    markAllNotificationsAsRead: builder.mutation<any, void>({
      query: () => ({
        url: `/api/notifications/mark-all-read`,
        method: 'POST',
      }),
      invalidatesTags: ['Notifications', 'NotificationCount'],
    }),

    // ==================== Preferences ====================
    getUserPreferences: builder.query<
      UserNotificationPreference[],
      {
        notification_template_id?: string;
      }
    >({
      query: (params) => ({
        url: `/api/notifications/preferences`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => response.preferences,
      providesTags: ['NotificationPreferences'],
    }),

    updateUserPreference: builder.mutation<any, Partial<UserNotificationPreference>>({
      query: (preference) => ({
        url: `/api/notifications/preferences`,
        method: 'POST',
        body: preference,
      }),
      invalidatesTags: ['NotificationPreferences'],
    }),

    bulkUpdatePreferences: builder.mutation<any, { preferences: Partial<UserNotificationPreference>[] }>({
      query: (data) => ({
        url: `/api/notifications/preferences/bulk`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['NotificationPreferences'],
    }),

    // ==================== Enums ====================
    getNotificationEnums: builder.query<NotificationEnums, void>({
      query: () => ({
        url: `/api/notifications/enums`,
        method: 'GET',
      }),
      transformResponse: (response: NotificationEnums) => response,
    }),
  }),
});

export const {
  useGetNotificationTemplatesQuery,
  useCreateNotificationTemplateMutation,
  useDeleteNotificationTemplateMutation,
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetUserPreferencesQuery,
  useUpdateUserPreferenceMutation,
  useBulkUpdatePreferencesMutation,
  useGetNotificationEnumsQuery,
} = notificationApiSlice;
